import { useState, useEffect, useRef } from "react";
import { useJsApiLoader, GoogleMap } from "@react-google-maps/api";
import { GOOGLE_MAPS_LOADER_OPTIONS } from "../utils/googleMaps";
import { MapPinIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { toDateSafe, formatEventDate } from "./formSectionHelpers";

// ---------------------------------------------------------------------------
// HostPinPickerModal — draggable-pin map picker (copied pattern from
// Chat.js LocationPickerModal). The user pans the map under a fixed center pin;
// onConfirm reports the current center + a <=60 char label.
// ---------------------------------------------------------------------------
export const HostPinPickerModal = ({ hostPin, onConfirm, onClose }) => {
  const DEFAULT_CENTER =
    hostPin && typeof hostPin.lat === "number" && typeof hostPin.lng === "number"
      ? { lat: hostPin.lat, lng: hostPin.lng }
      : { lat: 37.7749, lng: -122.4194 };
  // currentCenterRef tracks the map center without triggering re-renders.
  // Only `initialCenter` state is passed to <GoogleMap center=...> so setting it
  // doesn't loop back through onCenterChanged.
  const currentCenterRef = useRef(DEFAULT_CENTER);
  const [initialCenter, setInitialCenter] = useState(DEFAULT_CENTER);
  const [label, setLabel] = useState(hostPin?.label || "");
  const [geoError, setGeoError] = useState(null);
  const [relocating, setRelocating] = useState(false);
  const mapRef = useRef(null);

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  const fetchGPS = (onSuccess) => {
    if (!navigator.geolocation) {
      setGeoError("Geolocation is not supported by your browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        onSuccess(c);
      },
      (err) => {
        if (err.code === 1) {
          setGeoError("Location access denied. Drag the map to position the pin manually.");
        } else {
          setGeoError("Could not get your location. Drag the map to position the pin manually.");
        }
      },
      { enableHighAccuracy: false, timeout: 20000, maximumAge: 60000 }
    );
  };

  useEffect(() => {
    // Only auto-locate when there is no existing pin to edit.
    if (hostPin && typeof hostPin.lat === "number") return;
    fetchGPS((c) => {
      currentCenterRef.current = c;
      if (mapRef.current) {
        mapRef.current.panTo(c);
      } else {
        setInitialCenter(c);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRelocate = () => {
    setRelocating(true);
    fetchGPS((c) => {
      currentCenterRef.current = c;
      if (mapRef.current) mapRef.current.panTo(c);
      setRelocating(false);
    });
  };

  const handleCenterChanged = () => {
    if (mapRef.current) {
      const c = mapRef.current.getCenter();
      // Update ref only — no setState, so no re-render and no feedback loop.
      currentCenterRef.current = { lat: c.lat(), lng: c.lng() };
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Set group spot</h3>
          <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Map */}
        <div className="relative w-full h-56">
          {isLoaded ? (
            <>
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={initialCenter}
                zoom={16}
                onLoad={(m) => {
                  mapRef.current = m;
                }}
                onCenterChanged={handleCenterChanged}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                  zoomControl: true,
                  gestureHandling: "greedy",
                }}
              />
              {/* Fixed center pin */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="flex flex-col items-center" style={{ marginTop: "-24px" }}>
                  <svg width="28" height="36" viewBox="0 0 28 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 0C6.268 0 0 6.268 0 14c0 9.625 12.542 21.188 13.083 21.708a1.312 1.312 0 001.834 0C15.458 35.188 28 23.625 28 14 28 6.268 21.732 0 14 0z" fill="#EF4444" />
                    <circle cx="14" cy="14" r="5" fill="white" />
                  </svg>
                  <div className="w-1 h-2 bg-red-500 rounded-b" />
                </div>
              </div>
              {/* Hint */}
              <div className="absolute bottom-2 right-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-md px-2 py-1 text-[10px] text-gray-500 dark:text-gray-400 pointer-events-none">
                Drag map to position pin
              </div>
              {/* Relocate button */}
              <button
                type="button"
                onClick={handleRelocate}
                disabled={relocating}
                title="Go to my location"
                className="absolute bottom-2 left-2 w-[34px] h-[34px] rounded-full border-none bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm flex items-center justify-center cursor-pointer text-gray-700 dark:text-gray-200 shadow disabled:cursor-default disabled:opacity-70 hover:enabled:bg-white dark:hover:enabled:bg-gray-800 transition-colors"
              >
                {relocating ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-200 dark:border-gray-700 border-t-primary" />
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 8a4 4 0 100 8 4 4 0 000-8zm8.94 3A8.994 8.994 0 0013 3.06V1h-2v2.06A8.994 8.994 0 003.06 11H1v2h2.06A8.994 8.994 0 0011 20.94V23h2v-2.06A8.994 8.994 0 0020.94 13H23v-2h-2.06zM12 19a7 7 0 110-14 7 7 0 010 14z" />
                  </svg>
                )}
              </button>
            </>
          ) : (
            <div className="w-full h-full bg-gray-200 dark:bg-gray-700 animate-pulse flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          )}
        </div>

        <div className="px-4 py-3 flex flex-col gap-3">
          {geoError && (
            <p className="text-xs text-amber-600 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
              {geoError}
            </p>
          )}

          <input
            type="text"
            value={label}
            maxLength={60}
            onChange={(e) => setLabel(e.target.value.slice(0, 60))}
            onKeyDown={(e) => {
              // This modal lives inside the event <form>, so a bare Enter would
              // implicitly submit the whole editor. Swallow it and treat Enter as
              // "Set pin" (mirrors the button below), only once the map is ready.
              if (e.key === "Enter") {
                e.preventDefault();
                if (isLoaded) {
                  onConfirm(
                    currentCenterRef.current.lat,
                    currentCenterRef.current.lng,
                    label.trim() || null
                  );
                }
              }
            }}
            placeholder="Add a note – e.g. by the big oak tree"
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
          />

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() =>
                onConfirm(
                  currentCenterRef.current.lat,
                  currentCenterRef.current.lng,
                  label.trim() || null
                )
              }
              disabled={!isLoaded}
              className="flex-1 px-4 py-2 text-sm bg-primary text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-1"
            >
              <MapPinIcon className="w-4 h-4" />
              Set pin
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// DateChangeNotifyModal — opened from the inline notice. Offers a pre-drafted,
// editable heads-up; sending saves the event, then posts to the event chat
// (which onChatMessage fans out to SMS + push for everyone going).
// ---------------------------------------------------------------------------
export const DateChangeNotifyModal = ({ newDate, draft, onDraftChange, onSend, onClose, sending }) => {
  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={sending ? undefined : onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">
            Let attendees know the date changed?
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-4 flex flex-col gap-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            The date moved to{" "}
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {toDateSafe(newDate) ? formatEventDate(toDateSafe(newDate)) : "the new time"}
            </span>
            . Sending this <span className="font-medium">saves the event</span> and gives
            everyone who's going a heads-up — in the chat, plus SMS and push.
          </p>

          <div>
            <label
              htmlFor="dateChangeMessage"
              className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1"
            >
              Message to attendees
            </label>
            <textarea
              id="dateChangeMessage"
              value={draft}
              onChange={(e) => onDraftChange(e.target.value)}
              rows={6}
              disabled={sending}
              className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 shadow-sm focus:border-primary focus:ring-primary text-sm disabled:opacity-60"
            />
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="flex-1 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Not now
            </button>
            <button
              type="button"
              onClick={onSend}
              disabled={sending || !draft.trim()}
              className="flex-1 px-4 py-2 text-sm bg-primary text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {sending && (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/40 border-t-white" />
              )}
              {sending ? "Saving & sending…" : "Save & notify attendees"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
